// useRunEventStream — subscribes to GET /runs/:id/events?follow=true for a
// single run, parses NDJSON, and yields events to a callback. Tracks the last
// seen `idx` and reconnects on close with exponential backoff (capped at 30s).
//
// Reconnection strategy:
//   - Initial connect: no after_idx.
//   - On disconnect (network / non-terminal close): retry with after_idx set
//     to the last seen idx so the server can replay missed rows.
//   - On terminal status: stop. The server emits a synthetic `run_complete`
//     line on terminal transitions; we treat the stream's natural close as
//     the same signal.

import { useEffect, useRef } from 'react';
import * as api from '../lib/api';
import type { ApiEventLine } from '../lib/types';

interface Options {
  enabled: boolean;
  runId: string | null;
  /**
   * If set, the initial connect uses this as `after_idx` so the server skips
   * events the caller has already drained (e.g. via `fetchAllEvents`). Without
   * this, the live stream replays history on first connect and the caller
   * must dedupe by `idx`.
   */
  initialAfterIdx?: number;
  onEvent: (line: ApiEventLine) => void;
  onTerminal?: (line: ApiEventLine) => void;
}

const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 500;

export function useRunEventStream(opts: Options): void {
  const { enabled, runId, initialAfterIdx, onEvent, onTerminal } = opts;
  // Store callbacks in refs so we don't restart the stream on every render.
  const onEventRef = useRef(onEvent);
  const onTerminalRef = useRef(onTerminal);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onTerminalRef.current = onTerminal; }, [onTerminal]);

  useEffect(() => {
    if (!enabled || !runId) return;
    const ctrl = new AbortController();
    let stopped = false;
    let lastIdx = typeof initialAfterIdx === 'number' && initialAfterIdx >= 0
      ? initialAfterIdx
      : -1;
    let backoff = INITIAL_BACKOFF_MS;

    const loop = async (): Promise<void> => {
      while (!stopped) {
        try {
          const opts: api.StreamRunEventsOptions = {
            follow: true,
            signal: ctrl.signal,
          };
          if (lastIdx >= 0) opts.afterIdx = lastIdx;
          const stream = api.streamRunEvents(runId, opts);
          for await (const line of stream) {
            if (typeof line.idx === 'number' && line.idx > lastIdx) {
              lastIdx = line.idx;
            }
            // Reset backoff on first successful event after a reconnect.
            backoff = INITIAL_BACKOFF_MS;
            onEventRef.current(line);
            if (line.event === 'run_complete') {
              onTerminalRef.current?.(line);
              return;
            }
          }
          // Stream closed without a terminal event — could be a network drop
          // or server-side disconnect. Reconnect after backoff unless the
          // run actually finished.
          if (stopped) return;
        } catch (err) {
          if (stopped) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          // Surface the error as a synthetic event for the UI's diagnostics
          // pane, then back off and reconnect.
          onEventRef.current({
            event: 'stream_error',
            level: 'warn',
            message: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString(),
            run_id: runId,
          });
        }
        if (stopped) return;
        await new Promise<void>((r) => window.setTimeout(r, backoff));
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      }
    };

    void loop();

    return () => {
      stopped = true;
      ctrl.abort();
    };
  }, [enabled, runId]);
}
